import React, {
  useState,
  ChangeEvent,
} from 'react';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from 'pages/Dashboard/components/Input/Radio';
import Label from 'pages/Dashboard/components/Label';
import NumberTextField from 'core/components/NumberTextField';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  pluralize,
} from '@populateui/utils';
import {
  toEndTime,
} from 'core/components/DateTimePicker/dateTimeInterval';

type Props = {
  withLabel?: boolean;
};

function getEndOption(
  count?: number,
  endsOn?: string | null,
) {
  const result = !isNil(count) ? 'after' : 'never';

  return !isNil(endsOn) ? 'on' : result;
}

export default function EndsOnConditionFields({
  withLabel = true,
}: Props) {
  const {
    control,
    watch,
    setValue,
    clearErrors,
  } = useFormContext();

  const [
    count,
    eventDateTime,
    endsOn,
  ] = watch([
    'recurrence.count',
    'eventDateTime',
    'recurrence.endsOn',
  ]);

  const [endOption, setEndOption] = useState<string>(getEndOption(count ?? 0, endsOn) ?? 'never');

  const occurrencesCondition = endOption === 'after';
  const endDateCondition = endOption === 'on';
  const eventEndTime = eventDateTime?.startTime?.isValid()
    ? toEndTime(eventDateTime)
    : undefined;

  function onChange(e: ChangeEvent<HTMLInputElement>, value: string) {
    setEndOption(value);
    setValue('recurrence.count', value === 'after' ? 1 : undefined);
    setValue('recurrence.endsOn', value === 'on' ? eventEndTime?.add(1, 'month')?.tz() : undefined);
    clearErrors(['recurrence.endsOn', 'recurrence.count']);
  }

  return (
    <div className="my-3">
      {withLabel && <Label id="recurrence-end" label="Ends" />}
      <RadioGroup
        row
        className="w-full flex !pl-3 !gap-8 !items-center"
        value={endOption}
        onChange={onChange}
      >
        <Radio
          value="never"
          color="primary"
          label="Never"
        />
        <div className="flex gap-1 items-center">
          <Radio value="on" color="primary" label="On" />
          <Controller
            name="recurrence.endsOn"
            control={control}
            rules={{
              required: endDateCondition,
              validate: (value) => (endDateCondition ? !isNil(value) : true),
            }}
            render={({
              field,
              fieldState: { error },
            }) => (
              <Wrapper
                id="available-until"
                className="mt-1"
                error={!isNil(error)}
                showErrorText={false}
              >
                <DatePicker
                  {...field}
                  className="w-full"
                  containerClassName="w-full -mt-1.5"
                  showDateButtons={false}
                  disabled={!endDateCondition}
                  minDate={eventEndTime?.add(1, 'day').tz()}
                />
              </Wrapper>
            )}
          />
        </div>
        <div className="flex w-fit gap-2 items-center">
          <Radio value="after" color="primary" label="After" />
          <Controller
            name="recurrence.count"
            control={control}
            rules={{ required: occurrencesCondition }}
            render={({
              field,
              fieldState: { error },
            }) => (
              <Wrapper
                id="count"
                className="w-12"
                showErrorText={false}
                error={!isNil(error)}
              >
                <NumberTextField
                  {...field}
                  disabled={!occurrencesCondition}
                />
              </Wrapper>
            )}
          />
          <span className="text-xs -ml-1">
            {pluralize('Occurrence', Number(count), false)}
          </span>
        </div>
      </RadioGroup>
    </div>
  );
}
